import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { collection, deleteDoc, doc, getDocs, onSnapshot, query, serverTimestamp, setDoc, where, writeBatch } from "firebase/firestore";
import { Bell, CalendarClock, Download, ExternalLink, Mail, ShoppingBag } from "lucide-react";
import ConfirmDialog from "../../components/ConfirmDialog";
import MediaRenderer from "../../components/MediaRenderer";
import { useAuth } from "../../context/AuthContext";
import { db } from "../../firebase";
import { mergeSiteContent } from "../../utils/helpers";
import {
  ADMIN_EMAILS,
  BOOKING_STATUSES,
  DEFAULT_LATDIOR_PRODUCTS,
  DEFAULT_PRICING,
  LATDIOR_ORDER_STATUSES,
  ORDER_STATUSES,
  dateToKey,
  formatCurrency,
  getStudioSlots,
  mergePricing,
  normalizeLatdiorProduct,
} from "../../lib/platformData";
import {
  blockStudioSlots,
  retryWaitlistConfirmation,
  savePricing,
  unblockStudioSlot,
  updateDocument,
  updateStudioBookingStatus,
} from "../../lib/firestoreActions";
import { sanitizeForFirestore } from "../../lib/firebaseSafe";
import { uploadAdminMedia } from "../../lib/mediaUpload";
import { useToast } from "../../context/ToastContext";

function sortRecent(list) {
  return [...list].sort((a, b) => Number(b.createdAt?.seconds || 0) - Number(a.createdAt?.seconds || 0));
}

function Panel({ title, eyebrow, children, action }) {
  return (
    <section className="rounded-lg border border-white/10 bg-zinc-950 p-5 md:p-6">
      <div className="mb-5 flex flex-col justify-between gap-3 md:flex-row md:items-start">
        <div>
          {eyebrow && <p className="mb-2 text-xs font-bold uppercase tracking-[0.22em] text-red-500">{eyebrow}</p>}
          <h2 className="text-2xl font-black uppercase">{title}</h2>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function Field({ label, value, onChange, onBlur, type = "text", textarea = false, helper = "" }) {
  const Input = textarea ? "textarea" : "input";
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-gray-400">{label}</span>
      <Input
        type={type}
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
        onBlur={(event) => onBlur?.(event.target.value)}
        className={`w-full rounded border border-white/10 bg-black px-3 py-2 text-white outline-none focus:border-red-500 ${
          textarea ? "min-h-[96px]" : ""
        }`}
      />
      {helper && <span className="mt-1 block text-xs text-gray-500">{helper}</span>}
    </label>
  );
}

function StatusSelect({ value, labels, onChange }) {
  return (
    <select
      value={value || "pending"}
      onChange={(event) => onChange(event.target.value)}
      className="rounded border border-white/10 bg-black px-3 py-2 text-sm outline-none focus:border-red-500"
    >
      {Object.entries(labels).map(([key, label]) => (
        <option key={key} value={key}>
          {label}
        </option>
      ))}
    </select>
  );
}

function csvDownload(filename, rows) {
  const csv = rows
    .map((row) =>
      row
        .map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`)
        .join(",")
    )
    .join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function setNestedValue(source, path, value) {
  const keys = path.split(".");
  const clone = Array.isArray(source) ? [...source] : { ...source };
  let cursor = clone;

  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      cursor[key] = value;
      return;
    }
    const next = cursor[key];
    cursor[key] = Array.isArray(next) ? [...next] : { ...(next || {}) };
    cursor = cursor[key];
  });

  return clone;
}

function nestedPatch(path, value) {
  return path
    .split(".")
    .reverse()
    .reduce((acc, key) => ({ [key]: acc }), value);
}

export default function AdminPlatform() {
  const { user, loading, loginWithGoogle, logout } = useAuth();
  const location = useLocation();
  const isAdmin = Boolean(user && ADMIN_EMAILS.includes(user.email));
  const [pricing, setPricing] = useState(DEFAULT_PRICING);
  const [bookings, setBookings] = useState([]);
  const [slots, setSlots] = useState([]);
  const [serviceOrders, setServiceOrders] = useState([]);
  const [latdiorOrders, setLatdiorOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [messages, setMessages] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [users, setUsers] = useState([]);
  const [siteContent, setSiteContent] = useState(() => mergeSiteContent());
  const [blockForm, setBlockForm] = useState({
    date: dateToKey(new Date()),
    hours: [],
    reason: "",
  });
  const toast = useToast();

  useEffect(() => {
    if (!isAdmin) return undefined;
    const unsubs = [
      onSnapshot(doc(db, "settings", "pricing"), (snap) =>
        setPricing(mergePricing(snap.exists() ? snap.data() : DEFAULT_PRICING))
      ),
      onSnapshot(collection(db, "studioBookings"), (snap) =>
        setBookings(sortRecent(snap.docs.map((item) => ({ id: item.id, ...item.data() }))))
      ),
      onSnapshot(collection(db, "studioSlots"), (snap) =>
        setSlots(snap.docs.map((item) => ({ id: item.id, ...item.data() })))
      ),
      onSnapshot(collection(db, "serviceOrders"), (snap) =>
        setServiceOrders(sortRecent(snap.docs.map((item) => ({ id: item.id, ...item.data() }))))
      ),
      onSnapshot(collection(db, "latdiorOrders"), (snap) =>
        setLatdiorOrders(sortRecent(snap.docs.map((item) => ({ id: item.id, ...item.data() }))))
      ),
      onSnapshot(collection(db, "latdiorProducts"), (snap) =>
        setProducts(snap.docs.map((item) => normalizeLatdiorProduct({ id: item.id, ...item.data() })))
      ),
      onSnapshot(collection(db, "newsletterSubscribers"), (snap) =>
        setNewsletter(sortRecent(snap.docs.map((item) => ({ id: item.id, ...item.data() }))))
      ),
      onSnapshot(collection(db, "contactMessages"), (snap) =>
        setMessages(sortRecent(snap.docs.map((item) => ({ id: item.id, ...item.data() }))))
      ),
      onSnapshot(collection(db, "waitlistRequests"), (snap) =>
        setWaitlist(sortRecent(snap.docs.map((item) => ({ id: item.id, ...item.data() }))))
      ),
      onSnapshot(collection(db, "users"), (snap) =>
        setUsers(sortRecent(snap.docs.map((item) => ({ id: item.id, ...item.data() }))))
      ),
      onSnapshot(doc(db, "admin", "content"), (snap) =>
        setSiteContent(mergeSiteContent(snap.exists() ? snap.data() : {}))
      ),
    ];

    return () => unsubs.forEach((off) => off());
  }, [isAdmin]);

  const visibleSlots = useMemo(
    () => [...slots].sort((a, b) => `${a.date || ""}${a.hour || ""}`.localeCompare(`${b.date || ""}${b.hour || ""}`)),
    [slots]
  );
  const activeWaitlist = useMemo(() => {
    const seen = new Set();
    return waitlist.filter((item) => {
      if (item.status !== "waiting") return false;
      const key = `${item.productId || ""}:${String(item.email || "").trim().toLowerCase()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [waitlist]);
  const notificationCounts = useMemo(
    () => ({
      messages: messages.filter((item) => item.notificationRead !== true).length,
      bookings: bookings.filter((item) => item.status === "pending" && item.notificationRead !== true).length,
      orders: [...serviceOrders, ...latdiorOrders].filter((item) => ["pending", "paid"].includes(item.status) && item.notificationRead !== true).length,
      waitlist: activeWaitlist.filter((item) => item.notificationRead !== true).length,
    }),
    [activeWaitlist, bookings, latdiorOrders, messages, serviceOrders]
  );
  const totalNotifications =
    notificationCounts.messages +
    notificationCounts.bookings +
    notificationCounts.orders +
    notificationCounts.waitlist;
  const section = location.pathname.split("/").filter(Boolean).pop() || "plateforme";
  const isHub = section === "plateforme" || section === "admin";
  const show = (...names) => names.includes(section) || (isHub && names.includes("plateforme"));
  const sectionTitles = {
    admin: "Tableau de bord",
    plateforme: "Tableau de bord",
    services: "Services",
    reservations: "Réservations",
    commandes: "Commandes",
    latdior: "Latdior",
    newsletter: "Newsletter",
    messages: "Messages",
    notifications: "Notifications",
    tarifs: "Tarifs",
    parametres: "Paramètres",
  };
  const latdiorPage = siteContent.pages.latdior || {};

  const updateContentDraft = (path, value) => {
    setSiteContent((current) => mergeSiteContent(setNestedValue(current, path, value)));
  };

  const saveContentPath = async (path, value, successMessage = "Modification sauvegardée") => {
    try {
      await setDoc(
        doc(db, "admin", "content"),
        sanitizeForFirestore({
          ...nestedPatch(path, value),
          updatedAt: serverTimestamp(),
        }),
        { merge: true }
      );
      toast.success(successMessage);
    } catch (err) {
      toast.error("Sauvegarde impossible", err.message);
    }
  };

  const perform = async (action, successMessage) => {
    try {
      await action();
      toast.success(successMessage);
    } catch (err) {
      toast.error("Action impossible", err.message || "Réessaie dans un instant.");
    }
  };

  const markRead = (collectionName, id) => perform(
    () => updateDocument(collectionName, id, { notificationRead: true }),
    "Notification marquée comme lue"
  );

  const savePrices = async () => {
    await perform(() => savePricing({
      currency: "EUR",
      studioHourly: Number(pricing.studioHourly || 0),
      studioWithEngineer: Number(pricing.studioWithEngineer || pricing.studioHourly || 40),
      studioWithoutEngineer: Number(pricing.studioWithoutEngineer || 25),
      mixage: Number(pricing.mixage || 0),
      mastering: Number(pricing.mastering || 0),
      prod: Number(pricing.prod || 0),
    }), "Tarifs modifiés");
  };

  const toggleBlockHour = (hour) => {
    setBlockForm((current) => ({
      ...current,
      hours: current.hours.includes(hour)
        ? current.hours.filter((item) => item !== hour)
        : [...current.hours, hour].sort(),
    }));
  };

  const submitBlock = async () => {
    await perform(async () => {
      await blockStudioSlots(blockForm);
      setBlockForm((current) => ({ ...current, hours: [], reason: "" }));
    }, "Créneaux bloqués");
  };

  const saveProduct = async (product) => {
    await perform(async () => {
      if (product.available === true && products.find((item) => item.id === product.id)?.available === false) {
        await dedupeWaitlistForProduct(product.id);
      }
      await setDoc(
        doc(db, "latdiorProducts", product.id),
        sanitizeForFirestore({
          ...product,
          price: Number(product.price || 0),
          sizes: Array.isArray(product.sizes) ? product.sizes : String(product.sizes || "").split(",").map((x) => x.trim()).filter(Boolean),
          updatedAt: serverTimestamp(),
        }),
        { merge: true }
      );
    }, product.available === false ? "Produit marqué indisponible" : "Produit Latdior sauvegardé");
  };

  const dedupeWaitlistForProduct = async (productId) => {
    if (!productId) return;
    const snap = await getDocs(query(
      collection(db, "waitlistRequests"),
      where("productId", "==", productId),
      where("status", "==", "waiting")
    ));
    const byEmail = new Map();
    snap.docs.forEach((item) => {
      const email = String(item.data()?.email || "").trim().toLowerCase();
      if (!email) return;
      if (!byEmail.has(email)) byEmail.set(email, []);
      byEmail.get(email).push(item);
    });

    const batch = writeBatch(db);
    let duplicateCount = 0;
    byEmail.forEach((docs) => {
      if (docs.length <= 1) return;
      docs.slice(1).forEach((item) => {
        duplicateCount += 1;
        batch.update(item.ref, sanitizeForFirestore({
          status: "duplicate",
          duplicateOf: docs[0].id,
          notificationRead: true,
          updatedAt: serverTimestamp(),
        }));
      });
    });
    if (duplicateCount > 0) await batch.commit();
  };

  const initProducts = async () => {
    await Promise.all(DEFAULT_LATDIOR_PRODUCTS.map((product) => saveProduct(product)));
  };

  const addProduct = async () => {
    const product = {
      id: `latdior-${Date.now()}`,
      name: "Nouvel article Latdior",
      description: "",
      price: 0,
      image: "/images/latdior1.png",
      media: "/images/latdior1.png",
      mediaType: "image",
      sizes: ["S", "M", "L", "XL"],
      available: false,
      externalUrl: "https://latdiorparis.com",
    };
    await saveProduct(product);
  };

  const deleteProduct = async (product) => {
    await deleteDoc(doc(db, "latdiorProducts", product.id));
    toast.success("Article Latdior supprimé");
  };

  const resendWaitlistEmail = async (item) => {
    try {
      const result = await retryWaitlistConfirmation(item.id);
      toast.success(
        result.emailSent ? "Email d'alerte envoyé" : "Email remis en file d'attente",
        item.email || ""
      );
    } catch (err) {
      toast.error("Renvoi impossible", err.message || "Firebase n'a pas accepté l'envoi.");
    }
  };

  if (loading) return <main className="min-h-screen bg-black p-8 text-white">Chargement...</main>;

  if (!user) {
    return (
      <main className="grid min-h-screen place-items-center bg-black px-6 text-white">
        <section className="max-w-md rounded-lg border border-white/10 bg-zinc-950 p-6 text-center">
          <h1 className="text-3xl font-black uppercase">Connexion Admin</h1>
          <p className="mt-3 text-gray-400">Acces reserve a l'equipe Unlimited Session.</p>
          <button onClick={loginWithGoogle} className="mt-6 rounded-full bg-white px-5 py-3 text-sm font-bold uppercase text-black">
            Connexion Google
          </button>
        </section>
      </main>
    );
  }

  if (!isAdmin) {
    return (
      <main className="grid min-h-screen place-items-center bg-black px-6 text-white">
        <section className="max-w-md rounded-lg border border-white/10 bg-zinc-950 p-6 text-center">
          <h1 className="text-3xl font-black uppercase">Acces refuse</h1>
          <p className="mt-3 text-gray-400">{user.email} n'est pas dans la liste admin.</p>
          <button onClick={logout} className="mt-6 rounded-full bg-red-600 px-5 py-3 text-sm font-bold uppercase">
            Deconnexion
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-black p-4 text-white md:p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <header className="flex flex-col justify-between gap-4 rounded-lg border border-white/10 bg-zinc-950 p-5 md:flex-row md:items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-red-500">Admin plateforme</p>
            <h1 className="mt-2 text-3xl font-black uppercase md:text-5xl">
              {sectionTitles[section] || "Gestion complète"}
            </h1>
            <p className="mt-2 text-sm text-gray-400">{user.email}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/admin/notifications" className="relative rounded-full border border-white/15 px-4 py-2 text-sm font-bold uppercase">
              Notifications
              {totalNotifications > 0 && (
                <span className="absolute -right-2 -top-2 grid h-5 min-w-5 place-items-center rounded-full bg-red-600 px-1 text-xs">
                  {totalNotifications}
                </span>
              )}
            </Link>
            {!isHub && <Link to="/admin" className="rounded-full border border-white/15 px-4 py-2 text-sm font-bold uppercase">Tableau de bord</Link>}
          </div>
        </header>

        {show("plateforme", "notifications") && <section id="notifications" className="grid grid-cols-1 gap-4 md:grid-cols-4">
          {[
            { label: "Messages", value: notificationCounts.messages, icon: Mail, to: "/admin/messages" },
            { label: "Réservations", value: notificationCounts.bookings, icon: CalendarClock, to: "/admin/reservations" },
            { label: "Commandes", value: notificationCounts.orders, icon: ShoppingBag, to: "/admin/commandes" },
            { label: "Alertes dispo", value: notificationCounts.waitlist, icon: Bell, to: "/admin/notifications" },
          ].map((item) => {
            const Icon = item.icon;
            return (
              <Link key={item.label} to={item.to} className="rounded-lg border border-white/10 bg-zinc-950 p-5 transition hover:border-red-500/50 hover:bg-zinc-900">
                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-white text-black">
                  <Icon size={18} />
                </div>
                <p className="text-sm font-bold uppercase tracking-wide text-gray-500">{item.label}</p>
                <p className="mt-2 text-4xl font-black">{item.value}</p>
              </Link>
            );
          })}
        </section>}

        {isHub && <section className="grid gap-4 md:grid-cols-3">
          {[
            { title: "Contenu", links: [["Accueil", "/admin/accueil"], ["News", "/admin/news"], ["Artistes", "/admin/artistes"], ["Studio", "/admin/studio"], ["Vidéos", "/admin/videos"]] },
            { title: "Boutique et demandes", links: [["Latdior", "/admin/latdior"], ["Services", "/admin/services"], ["Réservations", "/admin/reservations"], ["Commandes", "/admin/commandes"]] },
            { title: "Réglages", links: [["Pages", "/admin/pages"], ["Tarifs", "/admin/tarifs"], ["Typographie", "/admin/typographie"], ["Mentions légales", "/admin/mentions-legales"], ["Paramètres", "/admin/parametres"]] },
          ].map((group) => <div key={group.title} className="border-t border-white/15 pt-5">
            <h2 className="mb-4 text-lg font-black uppercase">{group.title}</h2>
            <div className="grid gap-2">
              {group.links.map(([label, to]) => <Link key={to} to={to} className="rounded border border-white/10 bg-zinc-950 px-4 py-3 text-sm font-semibold text-zinc-300 transition hover:border-red-500/40 hover:text-white">{label}</Link>)}
            </div>
          </div>)}
        </section>}

        {show("tarifs", "services") && <Panel title="Tarifs" eyebrow="Prix modifiables">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
            <Field label="Studio avec ingénieur" type="number" value={pricing.studioWithEngineer ?? pricing.studioHourly} onChange={(v) => setPricing((p) => ({ ...p, studioWithEngineer: v, studioHourly: v }))} />
            <Field label="Studio sans ingénieur" type="number" value={pricing.studioWithoutEngineer} onChange={(v) => setPricing((p) => ({ ...p, studioWithoutEngineer: v }))} />
            <Field label="Mixage" type="number" value={pricing.mixage} onChange={(v) => setPricing((p) => ({ ...p, mixage: v }))} />
            <Field label="Mastering" type="number" value={pricing.mastering} onChange={(v) => setPricing((p) => ({ ...p, mastering: v }))} />
            <Field label="Prod" type="number" value={pricing.prod} onChange={(v) => setPricing((p) => ({ ...p, prod: v }))} />
          </div>
          <button onClick={savePrices} className="mt-5 rounded-full bg-white px-5 py-2 text-sm font-bold uppercase text-black">
            Sauvegarder les tarifs
          </button>
        </Panel>}

        {show("reservations") && <Panel title="Reservations studio" eyebrow="Validation admin">
          <div className="space-y-3">
            {bookings.length === 0 && <p className="text-gray-500">Aucune reservation.</p>}
            {bookings.map((booking) => (
              <article key={booking.id} className="rounded border border-white/10 bg-black p-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_auto] md:items-center">
                  <div>
                    <p className="font-bold">{booking.userName || booking.userEmail}</p>
                    <p className="text-sm text-gray-400">
                      {booking.sessionLabel || "Studio"} - {booking.date} - {booking.startTime || booking.hours?.[0]} / {booking.endTime || ""} - {formatCurrency(booking.total, booking.currency)}
                    </p>
                    <p className="text-xs uppercase tracking-wide text-gray-500">{booking.userEmail}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <StatusSelect
                      value={booking.status}
                      labels={BOOKING_STATUSES}
                      onChange={(value) => perform(() => updateStudioBookingStatus(booking, value), "Réservation mise à jour")}
                    />
                    <button onClick={() => perform(() => updateStudioBookingStatus(booking, "confirmed"), "Réservation confirmée")} className="rounded-full bg-green-600 px-3 py-2 text-xs font-bold uppercase">
                      Confirmer
                    </button>
                    <button onClick={() => perform(() => updateStudioBookingStatus(booking, "refused"), "Réservation refusée")} className="rounded-full bg-red-600 px-3 py-2 text-xs font-bold uppercase">
                      Refuser
                    </button>
                    {booking.notificationRead !== true && <button onClick={() => markRead("studioBookings", booking.id)} className="rounded-full border border-white/15 px-3 py-2 text-xs font-bold uppercase">Marquer lu</button>}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </Panel>}

        {show("reservations") && <Panel title="Blocage calendrier" eyebrow="Creneaux pris et blocages">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[360px_1fr]">
            <div className="rounded border border-white/10 bg-black p-4">
              <Field label="Date" type="date" value={blockForm.date} onChange={(v) => setBlockForm((current) => ({ ...current, date: v }))} />
              <div className="mt-4 grid max-h-60 grid-cols-2 gap-2 overflow-y-auto">
                {getStudioSlots().map((hour) => (
                  <button
                    key={hour}
                    onClick={() => toggleBlockHour(hour)}
                    className={`rounded border px-3 py-2 text-sm font-bold ${
                      blockForm.hours.includes(hour) ? "border-red-500 bg-red-600" : "border-white/10 bg-zinc-950"
                    }`}
                  >
                    {hour}
                  </button>
                ))}
              </div>
              <div className="mt-4">
                <Field label="Raison" value={blockForm.reason} onChange={(v) => setBlockForm((current) => ({ ...current, reason: v }))} />
              </div>
              <button onClick={submitBlock} className="mt-4 w-full rounded-full bg-white px-5 py-2 text-sm font-bold uppercase text-black">
                Bloquer
              </button>
            </div>

            <div className="max-h-[420px] overflow-y-auto rounded border border-white/10 bg-black p-4">
              {visibleSlots.length === 0 && <p className="text-gray-500">Aucun creneau pris.</p>}
              <div className="space-y-2">
                {visibleSlots.map((slot) => (
                  <div key={slot.id} className="flex items-center justify-between gap-3 rounded border border-white/10 bg-zinc-950 px-3 py-2 text-sm">
                    <span>{slot.date} - {slot.hour}</span>
                    <span className="text-gray-400">{slot.status}</span>
                    {slot.status === "blocked" && (
                      <button onClick={() => perform(() => unblockStudioSlot(slot.id), "Créneau débloqué")} className="rounded bg-red-600 px-2 py-1 text-xs font-bold uppercase">
                        Debloquer
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Panel>}

        {show("services", "commandes") && <Panel title="Commandes services" eyebrow="Mixage / mastering / prod">
          <div className="space-y-3">
            {serviceOrders.length === 0 && <p className="text-gray-500">Aucune commande service.</p>}
            {serviceOrders.map((order) => (
              <article key={order.id} className="rounded border border-white/10 bg-black p-4">
                <div className="flex flex-col justify-between gap-3 md:flex-row md:items-start">
                  <div>
                    <p className="font-bold">{order.serviceName} - {formatCurrency(order.total, order.currency)}</p>
                    <p className="text-sm text-gray-400">{order.userEmail}</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {(order.files || []).map((file) => (
                        <a key={file.path || file.url} href={file.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full border border-white/15 px-3 py-1 text-xs">
                          {file.name} <ExternalLink size={12} />
                        </a>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusSelect
                      value={order.status}
                      labels={ORDER_STATUSES}
                      onChange={(value) => perform(() => updateDocument("serviceOrders", order.id, { status: value }), "Statut de commande modifié")}
                    />
                    {order.notificationRead !== true && <button onClick={() => markRead("serviceOrders", order.id)} className="rounded border border-white/15 px-3 py-2 text-xs font-bold uppercase">Marquer lu</button>}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </Panel>}

        {show("latdior") && <Panel title="Zone Latdior" eyebrow="Page publique">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {[
              ["Badge", "badge"],
              ["Titre", "title"],
              ["Bouton principal", "ctaLabel"],
              ["Lien bouton principal", "ctaUrl"],
              ["Bouton secondaire", "secondaryLabel"],
              ["Drop", "drop"],
              ["Mood", "mood"],
              ["Statut", "status"],
            ].map(([label, key]) => (
              <Field
                key={key}
                label={label}
                value={latdiorPage[key]}
                onChange={(value) => updateContentDraft(`pages.latdior.${key}`, value)}
                onBlur={(value) => saveContentPath(`pages.latdior.${key}`, value, "Zone Latdior sauvegardée")}
              />
            ))}
            <div className="lg:col-span-2">
              <Field
                label="Description"
                value={latdiorPage.description}
                textarea
                onChange={(value) => updateContentDraft("pages.latdior.description", value)}
                onBlur={(value) => saveContentPath("pages.latdior.description", value, "Description Latdior sauvegardée")}
                helper="Ce texte est affiché dans le hero Latdior. Tu peux écrire long, il sera justifié côté public."
              />
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-3">
            {[
              ["Hero Latdior", "latdiorHero"],
              ["Infos drop", "latdiorStats"],
              ["Collection", "latdiorCollection"],
            ].map(([label, key]) => (
              <label key={key} className="flex items-center justify-between gap-3 rounded border border-white/10 bg-black px-4 py-3 text-sm font-bold uppercase">
                <span>{label}</span>
                <input
                  type="checkbox"
                  checked={siteContent.visibility?.[key] !== false}
                  onChange={async (event) => {
                    const value = event.target.checked;
                    updateContentDraft(`visibility.${key}`, value);
                    await saveContentPath(`visibility.${key}`, value, value ? "Section Latdior réactivée" : "Section Latdior masquée");
                  }}
                />
              </label>
            ))}
          </div>
        </Panel>}

        {show("latdior") && <Panel
          title="Produits Latdior"
          eyebrow="Catalogue"
          action={
            <div className="flex flex-wrap gap-2">
              <button onClick={addProduct} className="rounded-full bg-white px-4 py-2 text-xs font-bold uppercase text-black">Ajouter un article</button>
              <button onClick={initProducts} className="rounded-full border border-white/15 px-4 py-2 text-xs font-bold uppercase">Initialiser exemples</button>
            </div>
          }
        >
          <p className="readable-copy mb-5 max-w-3xl text-sm text-gray-400">
            Modifie ici les articles visibles sur la page Latdior : nom, prix, description, média, lien externe,
            disponibilité et suppression avec confirmation. Les produits indisponibles restent visibles côté client,
            mais l'achat est bloqué et le bouton d'alerte email apparaît.
          </p>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {(products.length ? products : DEFAULT_LATDIOR_PRODUCTS).map((product) => (
              <ProductEditor key={product.id} product={product} onSave={saveProduct} onDelete={deleteProduct} />
            ))}
          </div>
        </Panel>}

        {show("latdior", "commandes") && <Panel title="Commandes Latdior" eyebrow="Vetements">
          <div className="space-y-3">
            {latdiorOrders.length === 0 && <p className="text-gray-500">Aucune commande Latdior.</p>}
            {latdiorOrders.map((order) => (
              <article key={order.id} className="rounded border border-white/10 bg-black p-4">
                <div className="flex flex-col justify-between gap-3 md:flex-row md:items-start">
                  <div>
                    <p className="font-bold">{order.userEmail} - {formatCurrency(order.total, order.currency)}</p>
                    <ul className="mt-2 text-sm text-gray-400">
                      {(order.items || []).map((item, index) => (
                        <li key={`${item.productId}-${index}`}>{item.name} {item.size ? `(${item.size})` : ""} x{item.quantity}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusSelect
                      value={order.status}
                      labels={LATDIOR_ORDER_STATUSES}
                      onChange={(value) => perform(() => updateDocument("latdiorOrders", order.id, { status: value }), "Statut de commande modifié")}
                    />
                    {order.notificationRead !== true && <button onClick={() => markRead("latdiorOrders", order.id)} className="rounded border border-white/15 px-3 py-2 text-xs font-bold uppercase">Marquer lu</button>}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </Panel>}

        {show("newsletter", "messages") && <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {show("newsletter") && <Panel
            title="Newsletter"
            eyebrow="Inscrits"
            action={
              <button
                onClick={() => csvDownload("newsletter-unlimited.csv", [["email", "status"], ...newsletter.map((item) => [item.email, item.status])])}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-bold uppercase"
              >
                <Download size={14} /> Export CSV
              </button>
            }
          >
            <div className="max-h-80 space-y-2 overflow-y-auto">
              {newsletter.length === 0 && <p className="text-gray-500">Aucun inscrit.</p>}
              {newsletter.map((item) => (
                <div key={item.id} className="rounded border border-white/10 bg-black px-3 py-2 text-sm">
                  {item.email}
                </div>
              ))}
            </div>
          </Panel>}

          {show("messages") && <Panel title="Messages contact" eyebrow="Formulaire">
            <div className="max-h-80 space-y-3 overflow-y-auto">
              {messages.length === 0 && <p className="text-gray-500">Aucun message.</p>}
              {messages.map((message) => (
                <article key={message.id} className="rounded border border-white/10 bg-black p-3">
                  <div className="flex justify-between gap-3">
                    <div>
                      <p className="font-bold">{message.subject || "Contact"}</p>
                      <p className="text-sm text-gray-400">{message.name} - {message.email}</p>
                    </div>
                    <button onClick={() => perform(() => updateDocument("contactMessages", message.id, { status: "read", notificationRead: true }), "Message marqué comme lu")} className="rounded border border-white/15 px-2 py-1 text-xs uppercase">
                      {message.notificationRead ? "Lu" : "Marquer lu"}
                    </button>
                  </div>
                  <p className="mt-3 text-sm text-gray-300">{message.message}</p>
                </article>
              ))}
            </div>
          </Panel>}
        </div>}

        {show("notifications", "parametres", "latdior") && <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {show("notifications", "latdior") && <Panel title="Demandes dispo" eyebrow="Waitlist Latdior">
            <div className="max-h-80 space-y-2 overflow-y-auto">
              {activeWaitlist.length === 0 && <p className="text-gray-500">Aucune demande active.</p>}
              {activeWaitlist.map((item) => (
                <div key={item.id} className="rounded border border-white/10 bg-black px-3 py-2 text-sm">
                  <div className="flex flex-col justify-between gap-3 md:flex-row md:items-start">
                    <div>
                      <p className="font-bold">{item.productName || item.productId || "Produit"}</p>
                      <p className="text-gray-400">{item.email}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => resendWaitlistEmail(item)}
                        className="rounded-full border border-white/15 px-3 py-1 text-xs font-bold uppercase transition hover:border-white hover:bg-white hover:text-black"
                      >
                        Renvoyer l'email
                      </button>
                      {item.notificationRead !== true && <button onClick={() => markRead("waitlistRequests", item.id)} className="rounded-full border border-white/15 px-3 py-1 text-xs font-bold uppercase">Marquer lu</button>}
                    </div>
                  </div>
                  <p className={`mt-1 text-xs font-bold uppercase tracking-wide ${
                    item.customerEmailSent
                      ? "text-emerald-400"
                      : item.emailDispatchStatus === "error"
                        ? "text-red-300"
                        : "text-yellow-300"
                  }`}>
                    {item.customerEmailSent
                      ? "Email de confirmation envoyé"
                      : item.emailDispatchStatus === "error"
                        ? "Erreur pendant l'envoi email"
                        : "Email de confirmation en attente"}
                  </p>
                  {item.emailError && <p className="mt-1 text-xs text-red-300">{item.emailError}</p>}
                </div>
              ))}
            </div>
          </Panel>}

          {show("parametres") && <Panel title="Utilisateurs" eyebrow="Comptes">
            <div className="max-h-80 space-y-2 overflow-y-auto">
              {users.length === 0 && <p className="text-gray-500">Aucun utilisateur.</p>}
              {users.map((item) => (
                <div key={item.id} className="rounded border border-white/10 bg-black px-3 py-2 text-sm">
                  <p className="font-bold">{item.displayName || "Utilisateur"}</p>
                  <p className="text-gray-400">{item.email}</p>
                </div>
              ))}
            </div>
          </Panel>}
        </div>}
      </div>
    </main>
  );
}

function ProductEditor({ product, onSave, onDelete }) {
  const [draft, setDraft] = useState(product);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  useEffect(() => {
    setDraft(product);
  }, [product]);

  const uploadMedia = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const media = await uploadAdminMedia(file, "site/latdior");
      setDraft((current) => ({
        ...current,
        media,
        image: file.type.startsWith("image/") ? media : current.image,
        mediaType: file.type.startsWith("video/") ? "video" : "image",
      }));
      toast.success("Média Latdior prêt", "Sauvegarde le produit pour publier la modification.");
    } catch (err) {
      toast.error("Upload impossible", err.message);
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (saving || uploading) return;
    setSaving(true);
    try {
      await onSave(draft);
    } finally {
      setSaving(false);
    }
  };

  return (
    <article className="rounded border border-white/10 bg-black p-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-[120px_1fr]">
        <MediaRenderer
          src={draft.media || draft.image}
          type={draft.mediaType || "image"}
          alt=""
          background
          className="h-32 w-full rounded object-cover md:h-full"
        />
        <div className="space-y-3">
          <Field label="Nom" value={draft.name} onChange={(v) => setDraft((p) => ({ ...p, name: v }))} />
          <Field label="Description" value={draft.description} textarea onChange={(v) => setDraft((p) => ({ ...p, description: v }))} />
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Field label="Prix" type="number" value={draft.price} onChange={(v) => setDraft((p) => ({ ...p, price: v }))} />
            <Field
              label="Tailles"
              value={(Array.isArray(draft.sizes) ? draft.sizes : []).join(", ")}
              onChange={(v) => setDraft((p) => ({ ...p, sizes: v.split(",").map((x) => x.trim()).filter(Boolean) }))}
            />
          </div>
          <Field label="URL média" value={draft.media || draft.image} onChange={(v) => setDraft((p) => ({ ...p, media: v, image: p.mediaType === "video" ? p.image : v }))} />
          <Field label="Lien externe optionnel" value={draft.externalUrl} onChange={(v) => setDraft((p) => ({ ...p, externalUrl: v }))} />
          <div className="flex flex-wrap gap-3">
            <label className="inline-flex items-center gap-2 rounded-full border border-white/15 px-3 py-2 text-xs font-bold uppercase">
              <input
                type="checkbox"
                checked={draft.available !== false}
                onChange={(event) => setDraft((p) => ({ ...p, available: event.target.checked }))}
              />
              Disponible
            </label>
            <label className="inline-flex items-center gap-2 rounded-full border border-white/15 px-3 py-2 text-xs font-bold uppercase">
              Type
              <select
                value={draft.mediaType || "image"}
                onChange={(event) => setDraft((p) => ({ ...p, mediaType: event.target.value }))}
                className="bg-black outline-none"
              >
                <option value="image">Image</option>
                <option value="video">Vidéo</option>
              </select>
            </label>
            <label className={`cursor-pointer rounded-full border border-white/15 px-3 py-2 text-xs font-bold uppercase ${uploading ? "pointer-events-none opacity-60" : ""}`}>
              {uploading ? "Upload..." : "Importer média"}
              <input
                type="file"
                accept="image/*,video/*"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  uploadMedia(file);
                }}
              />
            </label>
            <button onClick={save} disabled={saving || uploading} className="rounded-full bg-white px-4 py-2 text-xs font-bold uppercase text-black disabled:opacity-50">
              {saving ? "Sauvegarde..." : "Sauvegarder"}
            </button>
            <button onClick={() => setConfirmOpen(true)} className="rounded-full bg-red-600 px-4 py-2 text-xs font-bold uppercase">
              Supprimer
            </button>
          </div>
        </div>
      </div>
      <ConfirmDialog
        open={confirmOpen}
        title="Supprimer cet article ?"
        message="Es-tu sûr de vouloir supprimer cet article ? Cette action est irréversible."
        confirmLabel="Supprimer"
        onConfirm={() => {
          setConfirmOpen(false);
          onDelete(draft);
        }}
        onCancel={() => setConfirmOpen(false)}
      />
    </article>
  );
}
